import { AddCircle } from '@mui/icons-material';
import { Box, Button, TextField } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useCreateDeviceCollection } from '~/api/device-collections/create';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { useAppContext } from '~/contexts';
import { useDeviceFiltersParams } from '../context';

export interface NewFilterDialogProps {
  onClose: () => void;
  open: boolean;
}

export const NewFilterDialog = ({ onClose, open }: NewFilterDialogProps) => {
  const { currentNetwork } = useAppContext();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState<string>('');
  const [createDeviceCollection] = useCreateDeviceCollection();
  const filterParams = useDeviceFiltersParams();
  const { t } = useTranslation();

  const conditions = useMemo(
    () => ({ ...filterParams, networkId: currentNetwork.id, search: searchParams.get('search') }),
    [currentNetwork.id, filterParams, searchParams],
  );

  const handleClose = useCallback(() => {
    onClose();
    setName('');
  }, [onClose]);

  const handleSave = async () => {
    if (!name) return;

    await createDeviceCollection({
      variables: {
        input: { conditions, name },
        source: 'saved_filter',
      },
    });
    handleClose();
  };

  return (
    <Dialog maxWidth="xs" fullWidth onClose={handleClose} open={open}>
      <DialogTitle onClose={handleClose}>{t('pages.devices.filters.new_filter_title')}</DialogTitle>

      <DialogContent>
        <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
          <TextField
            autoFocus
            fullWidth
            label={t('pages.devices.filters.filter_name_label')}
            placeholder={t('pages.devices.filters.filter_name_placeholder')}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          {t('common.dialogs.cancel')}
        </Button>
        <Button
          color="primary"
          disabled={!name}
          startIcon={<AddCircle />}
          variant="contained"
          onClick={handleSave}
        >
          {t('pages.devices.filters.save_filter')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
